import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { Paragraph, Spacer, TextButton, IconChevronUp, IconChevronDown, Anchor } from '@zillow/constellation';
import API from '@zillow/mortgage-api-client';
import { yieldCallback } from '@zillow/yield-callback';
import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { commaSeparateNumber } from '../../utils/zhl-calculator.js';
import LegalPopover from '../CalculatorForm/LegalPopover.js';
import CheckboxField from '../Fields/CheckboxField.js';
import SelectField from '../Fields/SelectField.js';
import TextField from '../Fields/TextField.js';
import DownPaymentField from './DownPaymentField.js';
import PropertyTaxField from './PropertyTaxField.js';
import { sendFieldInteractionEvent, legalPopoverInteractionEventBlock } from './VALoanCalculator.Clickstream.js';
import { useLastValidPayemntValues, FieldConfig, fhaCalculatorDisclaimerText, mortgageCalculatorDisclaimerText, vaCalculatorDisclaimerText } from './VALoanCalculator.utils.js';
import { setServerUrl, createMax, fetchRate } from '../../utils/mortgagehelpers/mortgage-helpers.js';
import { useCurrentRatesButtonUrl } from '../ClosingCostCalculator/useCurrentRatesButtonUrl.js';

function VALoanCalculatorFields(_ref) {
  var isFHAEnabled = _ref.isFHAEnabled,
    isMortgageComponent = _ref.isMortgageComponent;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    advanced = _useState2[0],
    setAdvanced = _useState2[1];
  var clickStreamBlockId = isMortgageComponent ? 'mortgage_calc' : 'va_mortgage_calc';
  var legalPopoverInteractionEventCmsInfo = legalPopoverInteractionEventBlock(clickStreamBlockId);
  var form = useFormContext();
  var lastValidValues = useLastValidPayemntValues(form);
  var fieldInterestRate = form.register(FieldConfig.interestRate.name, FieldConfig.interestRate.config);
  useEffect(function () {
    // Set the server URL and create the max object
    setServerUrl();
    createMax();
    if (fieldInterestRate) {
      var getLatestRate = function getLatestRate() {
        return new Promise(function ($return, $error) {
          var rate;
          return Promise.resolve(fetchRate({
            homePrice: lastValidValues.fields.homePrice,
            downPayment: Math.round(lastValidValues.fields.downPayment),
            va: isFHAEnabled ? false : isMortgageComponent ? false : true,
            fha: isFHAEnabled ? true : false,
            loanProgram: lastValidValues.fields.loanProgram
          })).then(function ($await_1) {
            try {
              rate = $await_1;
              form.setValue(fieldInterestRate.name, rate);
              return $return();
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }, $error);
        });
      };
      getLatestRate();
    }
  }, []);
  var currentRatesButtonUrl = useCurrentRatesButtonUrl('mortgage-rates', {
    value: lastValidValues.fields.homePrice,
    down: lastValidValues.fields.downPayment,
    auto: true,
    va: isFHAEnabled ? false : isMortgageComponent ? false : true,
    fha: isFHAEnabled ? true : false,
    zip: API.zipcode && API.zipcode.length === 5 ? API.zipcode : null,
    source: 'Z_Mortgage_Calc_rates'
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextField, {
    label: "Home price",
    name: FieldConfig.homePrice.name,
    leftAdornment: "$",
    config: FieldConfig.homePrice.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    onChange: function onChange() {
      return !isFHAEnabled && sendFieldInteractionEvent('Home price', 'form_field', clickStreamBlockId);
    },
    required: true
  }), /*#__PURE__*/React.createElement(DownPaymentField, {
    isFHAEnabled: isFHAEnabled,
    isMortgageComponent: isMortgageComponent
  }), !isFHAEnabled && /*#__PURE__*/React.createElement(TextField, {
    label: "ZIP code",
    name: FieldConfig.zipCode.name,
    autoComplete: "postal-code",
    onChange: function onChange() {
      sendFieldInteractionEvent('ZIP code', 'form_field', clickStreamBlockId);
    }
  }), /*#__PURE__*/React.createElement(SelectField, {
    options: isFHAEnabled ? FieldConfig.fhaLoanProgram.options : FieldConfig.loanProgram.options,
    label: "Loan program",
    name: FieldConfig.loanProgram.name,
    config: FieldConfig.loanProgram.config,
    onChange: function onChange() {
      return !isFHAEnabled && sendFieldInteractionEvent('Loan program', 'form_field', clickStreamBlockId);
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Interest rate",
    required: true,
    rightAdornment: "%",
    name: FieldConfig.interestRate.name,
    config: FieldConfig.interestRate.config,
    textButtonName: "See current rates",
    textButtonInteractionEventInfo: {
      block_type_cd: 'button',
      block_id: clickStreamBlockId,
      block_displayed_txt: 'See current rates'
    },
    textButtonHref: currentRatesButtonUrl,
    autoComplete: "off",
    tooltip: {
      title: 'Interest rate',
      body: isFHAEnabled ? /*#__PURE__*/React.createElement(Paragraph, null, "Representative interest rates are based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics.") : /*#__PURE__*/React.createElement(Paragraph, null, "Representative interest rates are based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics."),
      interactionEventCmsInfo: !isFHAEnabled && {
        block_type_cd: 'tooltip',
        block_id: clickStreamBlockId,
        block_displayed_txt: 'Interest rate'
      }
    },
    onChange: function onChange() {
      return !isFHAEnabled && sendFieldInteractionEvent('Interest rate', 'form_field', clickStreamBlockId);
    }
  }), /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "xs"
  }, /*#__PURE__*/React.createElement(TextButton, {
    icon: advanced ? /*#__PURE__*/React.createElement(IconChevronUp, null) : /*#__PURE__*/React.createElement(IconChevronDown, null),
    iconSize: "xs",
    type: "button",
    onClick: yieldCallback(function () {
      setAdvanced(!advanced);
      if (!isFHAEnabled) {
        if (advanced) {
          sendFieldInteractionEvent('Simple', 'toggle', clickStreamBlockId);
        } else {
          sendFieldInteractionEvent('Advanced', 'toggle', clickStreamBlockId);
        }
      }
    })
  }, advanced ? 'Simple' : 'Advanced')), advanced && /*#__PURE__*/React.createElement(React.Fragment, null, isFHAEnabled && /*#__PURE__*/React.createElement(CheckboxField, {
    label: "Include MIP",
    name: FieldConfig.includeMIP.name,
    tooltip: {
      title: 'Include MIP',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "FHA loans require two forms of mortgage insurance premiums (MIP) to protect lenders against losses that can occur when a borrower defaults on a mortgage loan. Annual MIP and Upfront MIP. This calculator will include Annual MIP.", ' ', /*#__PURE__*/React.createElement(Anchor, {
        href: "https://www.zillow.com/learn/fha-mortgage-insurance/",
        target: "_blank"
      }, "Learn more about MIP"), ".")
    }
  }), isMortgageComponent && /*#__PURE__*/React.createElement(CheckboxField, {
    label: "Include PMI",
    name: FieldConfig.includePMI.name,
    tooltip: {
      title: 'Private mortgage insurance',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Mortgage insurance is required primarily for borrowers on a conventional loan with a down payment of less than 20% of the home 's purchase price. It protects lenders against some or most of the losses that can occur when a borrower defaults on a mortgage loan. Contact your lender for more details."),
      interactionEventCmsInfo: !isFHAEnabled && {
        block_type_cd: 'tooltip',
        block_id: clickStreamBlockId,
        block_displayed_txt: 'Include PMI'
      }
    }
  }), /*#__PURE__*/React.createElement(CheckboxField, {
    label: "Include taxes/insurance",
    name: FieldConfig.includeTaxesInsurance.name,
    tooltip: {
      title: 'Taxes and insurance',
      body: isFHAEnabled ? /*#__PURE__*/React.createElement(Paragraph, null, "Includes property taxes, homeowners insurance, mortgage insurance premium, and homeowners association dues.") : /*#__PURE__*/React.createElement(Paragraph, null, "Includes property taxes, homeowners insurance, private mortgage insurance, and homeowners association dues."),
      interactionEventCmsInfo: !isFHAEnabled && {
        block_type_cd: 'tooltip',
        block_id: clickStreamBlockId,
        block_displayed_txt: 'Include taxes/insurance'
      }
    },
    onChange: function onChange() {
      return !isFHAEnabled && sendFieldInteractionEvent('Include taxes/insurance', 'toggle', clickStreamBlockId);
    }
  }), /*#__PURE__*/React.createElement(PropertyTaxField, {
    isFHAEnabled: isFHAEnabled
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Home insurance",
    required: true,
    leftAdornment: "$",
    rightAdornment: "/year",
    name: FieldConfig.homeInsurance.name,
    config: FieldConfig.homeInsurance.config,
    format: commaSeparateNumber,
    autoComplete: "off",
    tooltip: {
      title: 'Home insurance',
      body: isFHAEnabled ? /*#__PURE__*/React.createElement(Paragraph, null, "Commonly known as hazard insurance, most lenders require insurance to provide damage protection for your home and personal property from various events, including fire, lightning, burglary, vandalism, storms, explosions, and more. The personal liability coverage in standard homeowner's insurance policies is intended to protect against lawsuits involving injuries that occur on your property. Borrowers shop for their own home insurance.") : /*#__PURE__*/React.createElement(Paragraph, null, "Commonly known as hazard insurance, most lenders require insurance to provide damage protection for your home and personal property from a variety of events, including fire, lightning, burglary, vandalism, storms, explosions, and more. The personal liability coverage in standard homeowner's insurance policies is intended to protect against lawsuits involving injuries that occur on your property. Borrowers shop for their own home insurance. The VA calculator includes estimated home insurance premiums."),
      interactionEventCmsInfo: !isFHAEnabled && {
        block_type_cd: 'tooltip',
        block_id: clickStreamBlockId,
        block_displayed_txt: 'Home insurance'
      }
    },
    onChange: function onChange() {
      return !isFHAEnabled && sendFieldInteractionEvent('Home insurance', 'form_field', clickStreamBlockId);
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "HOA dues",
    required: true,
    leftAdornment: "$",
    rightAdornment: "/mo",
    name: FieldConfig.hoa.name,
    config: FieldConfig.hoa.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Homeowners association dues',
      body: isFHAEnabled ? /*#__PURE__*/React.createElement(Paragraph, null, "Some condominiums or townhome communities are owned by a homeowners association (HOA), which requires owners to pay a monthly fee to cover shared amenities or services, such as garbage collection, landscaping, snow removal and pool maintenance.") : /*#__PURE__*/React.createElement(Paragraph, null, "Typically, owners of condominiums or townhomes are required to pay homeowners association dues (known as HOA fees), to cover common amenities or services within the property such as garbage collection, landscaping, snow removal, pool maintenance, and hazard insurance."),
      interactionEventCmsInfo: !isFHAEnabled && {
        block_type_cd: 'tooltip',
        block_id: clickStreamBlockId,
        block_displayed_txt: 'HOA dues'
      }
    },
    onChange: function onChange() {
      return !isFHAEnabled && sendFieldInteractionEvent('HOA dues', 'form_field', clickStreamBlockId);
    }
  })), /*#__PURE__*/React.createElement(LegalPopover, {
    triggerText: "Calculator disclaimer",
    text: isFHAEnabled ? fhaCalculatorDisclaimerText : isMortgageComponent ? mortgageCalculatorDisclaimerText : vaCalculatorDisclaimerText,
    interactionEventCmsInfo: !isFHAEnabled ? legalPopoverInteractionEventCmsInfo : {}
  }));
}

export { VALoanCalculatorFields as default };
